// CMS Care Update — read-only accessors over the task row (spec 2026-08-26-care-update-auto-push).
// The note itself has ONE owner: WF70's Build Final Note, pinned by S-119.

import { templateCodeOf } from './task';

const CARE_UPDATE_TEMPLATES: Record<string, string> = {
  onb_care_plan_share: 'Care Initiation',
  cpc_care_plan_share_pcp: 'Update',
};

const OPEN_STATUSES = new Set(['pending', 'in_progress', 'scheduled']);

// Minimal structural view of a task row — callers pass WF50-normalized rows.
export interface TaskLike {
  status?: string | null;
  context_json?: unknown;
  [key: string]: unknown;
}

// Only the fields the app reads — the prose/measures/pcp blocks are WF70-internal.
export interface CareUpdateDraft {
  status?: 'generating' | 'ready' | 'error' | string;
  error?: string | null;
  // WF70 sets this when the visit-note feed is on and the chart holds no note to assess from.
  // Since 2026-08-27 the visit note is the ONLY chart-derived clinical source, so without one
  // there is no clinical narrative to write — the coordinator has to compose it. The strip says
  // that in words rather than showing a draft whose Assessment is a placeholder.
  no_visit_note?: boolean;
}

export interface CareUpdatePush {
  note_id: string;
  at?: string;
}

export function careUpdateTypeOf(task: TaskLike | null | undefined): string | null {
  if (!task) return null;
  return CARE_UPDATE_TEMPLATES[templateCodeOf(task)] ?? null;
}

// The one gating boolean both surfaces use.
export function isCareUpdateTask(task: TaskLike | null | undefined): boolean {
  return !!task && careUpdateTypeOf(task) !== null && OPEN_STATUSES.has(String(task.status));
}

function taskContextOf(task: TaskLike | null | undefined): Record<string, unknown> {
  const raw = task?.context_json;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Record<string, unknown>; } catch { return {}; }
  }
  return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw as Record<string, unknown> : {};
}

export function careUpdateDraftOf(task: TaskLike | null | undefined): CareUpdateDraft | null {
  const d = taskContextOf(task).care_update_draft;
  return (d && typeof d === 'object' && !Array.isArray(d)) ? d as CareUpdateDraft : null;
}

export function careUpdatePushesOf(task: TaskLike | null | undefined): CareUpdatePush[] {
  const cu = taskContextOf(task).care_update as { pushes?: CareUpdatePush[] } | undefined;
  return cu && Array.isArray(cu.pushes) ? cu.pushes : [];
}

export interface CareUpdatePushError { code?: string }

export function careUpdateLastPushErrorOf(task: TaskLike | null | undefined): CareUpdatePushError | null {
  const cu = taskContextOf(task).care_update as { last_push_error?: CareUpdatePushError } | undefined;
  return cu?.last_push_error ?? null;
}

// True when the draft exists but could not be written for want of a clinician visit note.
export function careUpdateNoVisitNote(task: TaskLike | null | undefined): boolean {
  return careUpdateDraftOf(task)?.no_visit_note === true;
}
